import { mockUserId } from "@/mock-api/data/userExample.ts";
import { jhvVmId, ubuntuId, windowsId } from "@/mock-api/data/vmIds.ts";
import { executeWithProbability } from "@/mock-api/executeWithProbability.ts";
import {
    currentDateTime,
    randomElement,
    randomPastDateTime,
    randomSubnetIpv6,
    randomTitle,
    randomUuid,
} from "@/mock-api/randomTypes.ts";
import type { VmInterface, Vpc } from "@warrenio/api-spec/spec.oats.gen";

export const vpc1Id = "a748335a-6c87-498f-80cb-3dae0becb59d";
export const vpcNoNameId = "15de427d-2c2c-433e-ad16-d7b07c3483d6";

export const vpcList1: Vpc[] = [
    {
        subnet: "10.46.191.0/24",
        name: "Default Network",
        created_at: "2022-02-04 15:47:59",
        updated_at: "2024-03-13 12:47:53",
        uuid: vpc1Id,
        type: "private",
        is_default: true,
        vm_uuids: [windowsId, ubuntuId],
        resources_count: 2,
        subnet_ipv6: "2a05:1cc0:10:3::5d:0/112",
    },
    // No resources
    {
        subnet: "10.112.197.0/24",
        name: "Demo Network",
        created_at: "2024-03-11 11:52:46",
        updated_at: "2024-03-11 11:52:46",
        uuid: "fe8b42eb-05ce-44e7-8122-e36b63ee75d0",
        type: "private",
        is_default: false,
        vm_uuids: [],
        resources_count: 0,
        subnet_ipv6: "2a05:1cc0:10:3::19a:0/112",
    },
];

const jhvNetwork = {
    subnet: "10.6.172.0/24",
    name: "Second Location Network 1",
    created_at: "2024-03-11 11:59:29",
    updated_at: "2024-03-13 12:47:53",
    uuid: "c9010197-7204-4aca-9c7b-30d130f8128e",
    type: "private",
    is_default: false,
    subnet_ipv6: "2a05:1cc0:10:3::19d:0/112",
} satisfies Omit<Vpc, "vm_uuids" | "resources_count">;

export const vpcList2: Vpc[] = [
    {
        ...jhvNetwork,
        vm_uuids: [jhvVmId],
        resources_count: 1,
    },
    {
        subnet: "10.55.144.0/24",
        name: "Second Location Network 2",
        created_at: "2024-03-12 14:59:28",
        updated_at: "2024-03-12 14:59:28",
        uuid: "8b1be00c-96c8-4751-80df-f6cdc2819010",
        type: "private",
        is_default: false,
        vm_uuids: [],
        resources_count: 0,
        subnet_ipv6: "2a05:1cc0:10:3::1a3:0/112",
    },
];

const sampleNetwork1 = {
    subnet: "10.2.189.0/24",
    created_at: "2021-03-01 12:44:36",
    updated_at: "2025-02-11 11:10:58",
    uuid: "d7a81347-48b1-4c32-98a3-4d8d58458757",
    type: "private",
    is_default: true,
    name: "Default network",
    subnet_ipv6: "2a05:1cc0:10:3::a:0/112",
};
const sampleNetwork2 = {
    subnet: "10.3.201.0/24",
    created_at: "2022-07-15 09:30:12",
    updated_at: "2025-06-25 17:05:43",
    uuid: "b94e2a66-9d71-41a6-baa5-735e812a3b2c",
    type: "private",
    is_default: true,
    name: "Main network",
    subnet_ipv6: "2a05:1cc0:10:7::b:0/112",
};
export const vmInterfaces: VmInterface[] = [
    {
        network: sampleNetwork1,
        private_ip_address: {
            id: 17295,
            address: "10.2.189.131",
            user_id: 136,
            type: "private",
            network_id: 10,
            enabled: true,
            created_at: "2023-09-29 12:17:16",
            updated_at: "2023-09-29 12:17:16",
            uuid: "026e9a5f-1b0a-474e-8a56-62338cb45f92",
            is_deleted: false,
            is_ipv6: false,
        },
        public_ipv6_address: {
            id: 17296,
            address: "2a05:1cc0:10:3::a:3",
            user_id: 136,
            type: "public",
            network_id: 10,
            enabled: true,
            created_at: "2023-09-29 12:17:16",
            updated_at: "2023-09-29 12:17:16",
            uuid: "62f012bc-0ddb-4ddd-b615-e2aa3c037bd7",
            is_deleted: false,
            is_ipv6: true,
        },
        subinterfaces: [
            {
                id: 54,
                uuid: "eba81810-8ce2-454a-8956-6c123f9b2d67",
                created_at: "2025-01-29 06:51:25",
                vmi_id: 8503,
                vlan_tag: 333,
                mac_addr: "02:eb:a8:18:10:8c",
                network_id: 86,
                network: {
                    subnet: "10.7.25.0/24",
                    created_at: "2021-12-02 11:06:48",
                    updated_at: "2025-01-30 14:29:31",
                    uuid: "15de427d-2c2c-433e-ad16-d7b07c3483d6",
                    type: "private",
                    is_default: false,
                    name: "Sample network",
                    subnet_ipv6: "2a05:1cc0:10:3::56:0/112",
                },
                ip_address: {
                    id: 29745,
                    address: "10.7.25.254",
                    user_id: 136,
                    type: "private",
                    network_id: 86,
                    enabled: true,
                    created_at: "2025-01-29 06:51:26",
                    updated_at: "2025-01-29 06:51:26",
                    uuid: "f0e2a7fa-ba2d-4914-a914-a45f7791461e",
                    is_deleted: false,
                    is_ipv6: false,
                },
            },
            {
                id: 83,
                uuid: "2362932d-841d-4c34-9076-6057ce6cb71f",
                created_at: "2025-01-30 14:29:07",
                vmi_id: 8503,
                vlan_tag: 222,
                mac_addr: "02:23:62:93:2d:84",
                network_id: 10,
                network: sampleNetwork1,
                ip_address: {
                    id: 29803,
                    address: "10.2.189.2",
                    user_id: 136,
                    type: "private",
                    network_id: 10,
                    enabled: true,
                    created_at: "2025-01-30 14:29:07",
                    updated_at: "2025-01-30 14:29:07",
                    uuid: "c3671212-a0f7-411d-af73-9089f212bea9",
                    is_deleted: false,
                    is_ipv6: false,
                },
            },
            {
                id: 81,
                uuid: "4773bfcb-3e41-4e89-81fa-301e144b0ac5",
                created_at: "2025-01-30 09:12:34",
                vmi_id: 8503,
                vlan_tag: 111,
                mac_addr: "02:47:73:bf:cb:3e",
                network_id: 10,
                network: sampleNetwork1,
                ip_address: {
                    id: 29786,
                    address: "10.2.189.5",
                    user_id: 136,
                    type: "private",
                    network_id: 10,
                    enabled: true,
                    created_at: "2025-01-30 09:12:35",
                    updated_at: "2025-01-30 09:12:35",
                    uuid: "cd7e0f61-2f1f-41e6-8cda-c3037ba44412",
                    is_deleted: false,
                    is_ipv6: false,
                },
            },
            {
                id: 77,
                uuid: "832b7848-55c6-4ed6-bb4f-134b0b214962",
                created_at: "2025-01-29 12:38:48",
                vmi_id: 8503,
                vlan_tag: 345,
                mac_addr: "02:83:2b:78:48:55",
                network_id: 10,
                network: sampleNetwork1,
                ip_address: {
                    id: 29779,
                    address: "10.2.189.3",
                    user_id: 136,
                    type: "private",
                    network_id: 10,
                    enabled: true,
                    created_at: "2025-01-29 12:38:49",
                    updated_at: "2025-01-29 12:38:49",
                    uuid: "45704e02-1fdd-4fd7-b13d-8f38e0279c93",
                    is_deleted: false,
                    is_ipv6: false,
                },
            },
        ],
        vmi_uuid: ubuntuId,
        vm_uuid: ubuntuId,
        vmi_mac: "52:54:00:6d:89:28",
    },
    {
        network: sampleNetwork2,
        private_ip_address: {
            id: 17295,
            address: "10.2.189.131",
            user_id: 136,
            type: "private",
            network_id: 10,
            enabled: true,
            created_at: "2023-09-29 12:17:16",
            updated_at: "2023-09-29 12:17:16",
            uuid: "026e9a5f-1b0a-474e-8a56-62338cb45f92",
            is_deleted: false,
            is_ipv6: false,
        },
        public_ipv6_address: {
            id: 17296,
            address: "2a05:1cc0:10:3::a:3",
            user_id: 136,
            type: "public",
            network_id: 10,
            enabled: true,
            created_at: "2023-09-29 12:17:16",
            updated_at: "2023-09-29 12:17:16",
            uuid: "62f012bc-0ddb-4ddd-b615-e2aa3c037bd7",
            is_deleted: false,
            is_ipv6: true,
        },
        subinterfaces: [
            {
                id: 54,
                uuid: "f3b8a87e-4c13-41ce-a731-2c4e0a5d6d9c",
                created_at: "2025-01-29 06:51:25",
                vmi_id: 8513,
                vlan_tag: 444,
                mac_addr: "02:eb:a8:18:10:8c",
                network_id: 86,
                network: sampleNetwork2,
                ip_address: {
                    id: 29745,
                    address: "10.7.25.254",
                    user_id: 136,
                    type: "private",
                    network_id: 86,
                    enabled: true,
                    created_at: "2025-01-29 06:51:26",
                    updated_at: "2025-01-29 06:51:26",
                    uuid: "f0e2a7fa-ba2d-4914-a914-a45f7791461e",
                    is_deleted: false,
                    is_ipv6: false,
                },
            },
            {
                id: 83,
                uuid: "7e9c5b10-2a34-4f93-a8e7-59d445c3f2d2",
                created_at: "2025-01-30 14:29:07",
                vmi_id: 8513,
                vlan_tag: 555,
                mac_addr: "02:23:62:93:2d:84",
                network_id: 10,
                network: sampleNetwork2,
                ip_address: {
                    id: 29803,
                    address: "10.2.189.2",
                    user_id: 136,
                    type: "private",
                    network_id: 10,
                    enabled: true,
                    created_at: "2025-01-30 14:29:07",
                    updated_at: "2025-01-30 14:29:07",
                    uuid: "c3671212-a0f7-411d-af73-9089f212bea9",
                    is_deleted: false,
                    is_ipv6: false,
                },
            },
        ],
        vmi_uuid: windowsId,
        vm_uuid: windowsId,
        vmi_mac: "3a:9f:4c:21:b7:8e",
    },
];

export const vmInterfaces2: VmInterface[] = [
    {
        network: jhvNetwork,
        private_ip_address: {
            id: 17297,
            address: "10.6.172.118",
            user_id: mockUserId,
            type: "private",
            network_id: 413,
            enabled: true,
            created_at: "2024-03-11 11:59:29",
            updated_at: "2024-03-13 12:47:53",
            uuid: "b9cf71b4-d8e4-4a30-9867-c4b95eb53275",
            is_deleted: false,
            is_ipv6: false,
        },
        public_ipv6_address: null,
        subinterfaces: [],
        vmi_uuid: "49afc6b0-b42c-4fb6-bbd0-57b478f41417",
        vm_uuid: jhvVmId,
        vmi_mac: "52:54:00:54:84:4a",
    },
];

export function generateNetwork(vpcList: Vpc[]): Vpc {
    let base = vpcList[0]; // with resources
    executeWithProbability(0.5, () => {
        base = vpcList[1]; // without resources
    });
    return {
        ...base,
        created_at: randomPastDateTime(),
        updated_at: currentDateTime(),
        uuid: randomUuid(),
        name: randomTitle(),
        type: randomElement(["public", "private"]),
        subnet_ipv6: randomSubnetIpv6(),
        is_default: false,
    };
}

export function setDefaultNetwork(vpc_list: Vpc[]): Vpc[] {
    const result = [...vpc_list];
    const randomIndex = Math.floor(Math.random() * vpc_list.length);
    result[randomIndex].is_default = true;
    return result;
}
